import React, { useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import orderBy from "lodash/orderBy";
import SERVICES from "utils/constants/services";
import { PRODUCT } from "utils/constants/models";
import { fetchIntegrations } from "actions/integration";
import { createMerchantAccountProductsFromIntegrationImport } from "actions/product";
import { fetchMerchantAccountProductsPrices } from "actions/merchantAccount";
import ModelSearchList from "components/ModelSearchList";
import { prepareShopifyProduct } from "utils/integrations/shopify";
import { prepareSquarespaceProduct } from "utils/integrations/squarespace";
import ImportProductCard from "./ImportProductCard";
import ImportProductCell from "./ImportProductCell";
import Select from "components/Form/fields/Select";
import Padder from "components/Padder";
import startCase from "lodash/startCase";
import { ensureIntegrations, ensureProductsPrices } from "utils/hooks";
import IndexActionLoader from "components/IndexActionLoader";
import { useCtxReferences } from "utils/selectors";
import { LIST_PRESENTATION, MAIN_CONTENT_MAX_WIDTH } from "utils/constants/ui";

const CONTEXT_SERVICES = [SERVICES.SQUARESPACE, SERVICES.SHOPIFY];
const TRANSFORMERS = {
  [SERVICES.SHOPIFY]: prepareShopifyProduct,
  [SERVICES.SQUARESPACE]: prepareSquarespaceProduct
};

const MODEL_SEARCH_CLASSES = {
  container: "h-100",
  inputWrapper: "flex flex-row items-center pb3",
  item: "mv2",
  option: {
    option: "f6"
  }
};

const ImportProductPicker = ({ actions, goBack }) => {
  const {
    integrations: allIntegrations,
    loadingIntegrations
  } = ensureIntegrations({
    fetchIntegrations: actions.fetchIntegrations
  });
  const {
    products: merchantProducts,
    loading: loadingProducts
  } = ensureProductsPrices({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices
  });

  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const indexLoading = loadingIntegrations || loadingProducts;
  const integrations = allIntegrations.filter(
    ({ service }) => CONTEXT_SERVICES.indexOf(service) > -1
  );
  const [loading, setLoading] = useState(false);
  const [service, setService] = useState(integrations[0].service);
  useEffect(() => {
    if (integrations && !service) {
      setService(integrations[0].service);
    }
  }, [integrations]);

  const integration = integrations.find((int) => int.service === service) || {};

  const transformer = TRANSFORMERS[service];
  const { raw: shop, products } = integration;
  const models =
    shop &&
    products &&
    orderBy(
      products.map((product) =>
        transformer({ shop, product, integration, merchantProducts })
      ),
      ["imported"]
    );

  const selectFieldProps = {
    options: CONTEXT_SERVICES.map((ctxService) => ({
      uid: ctxService,
      label: startCase(ctxService)
    })),
    value: service,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      setService(selection);
    }
  };

  const importAllIntegrationProducts = () => {
    setLoading(true);
    actions.createMerchantAccountProductsFromIntegrationImport(
      {
        resource,
        resourceUUID,
        uuid: merchantAccountUUID,
        data: {
          integration: {
            uuid: integration.uuid
          }
        }
      },
      (err) => {
        setLoading(false);
        if (!err) {
          goBack && goBack();
        }
      }
    );
  };

  let content;
  if (indexLoading) {
    content = <IndexActionLoader />;
  } else {
    content = (
      <div className="w-100 pt3">
        {integrations.length > 1 && <Select {...selectFieldProps} />}
        <ModelSearchList
          models={models || []}
          query={{
            model: PRODUCT,
            keys: ["name", "id"]
          }}
          customClasses={{
            [LIST_PRESENTATION.GRID]: MODEL_SEARCH_CLASSES,
            [LIST_PRESENTATION.LIST]: MODEL_SEARCH_CLASSES
          }}
          components={{
            [LIST_PRESENTATION.GRID]: ImportProductCell,
            [LIST_PRESENTATION.LIST]: ImportProductCard
          }}
          barActions={[
            {
              theme: "slim",
              copy: "Import all",
              loading,
              onClick: importAllIntegrationProducts
            }
          ]}
        />
        <Padder theme="visible" />
      </div>
    );
  }

  return <div className={MAIN_CONTENT_MAX_WIDTH}>{content}</div>;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchIntegrations,
      fetchMerchantAccountProductsPrices,
      createMerchantAccountProductsFromIntegrationImport
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ImportProductPicker);
